import type { DeviceConfigResponse } from "@shared/admin";
import { PosLocalRepository } from "./pos-repository";
import { DEFAULT_SYNC_RETRY_POLICY, isRetryableSyncStatus, withSyncRetry, type SyncRetryPolicy } from "./sync-retry";

export type DeviceConfigSyncResult = {
  changed: boolean;
  revision: number | null;
  error?: string;
};

export class DeviceConfigSyncClient {
  constructor(
    private readonly repository: PosLocalRepository,
    private readonly fetcher: typeof fetch = fetch,
    private readonly retryPolicy: SyncRetryPolicy = DEFAULT_SYNC_RETRY_POLICY,
    private readonly sleep: (delayMs: number) => Promise<void> = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
  ) {}

  async refresh(): Promise<DeviceConfigSyncResult> {
    const current = await this.repository.getDeviceConfigSnapshot();
    const configCorrelationId = crypto.randomUUID();
    let previousActionId: string | undefined;
    const response = await this.request<DeviceConfigResponse>(`/api/device/config?after=${encodeURIComponent(String(current?.configRevision ?? 0))}`, undefined, () => {
      const actionId = crypto.randomUUID();
      const retryOfActionId = previousActionId;
      previousActionId = actionId;
      return {
        headers: {
          "x-posnext-config-correlation-id": configCorrelationId,
          "x-posnext-config-action-id": actionId,
          ...(retryOfActionId ? { "x-posnext-config-retry-of-action-id": retryOfActionId } : {}),
        },
      };
    });
    if (!response.sync) throw new Error("Device Config sync trace is required");
    try {
      if (response.changed) await this.repository.applyDeviceConfigSnapshot(response.snapshot);
    } catch (error) {
      await this.request("/api/device/config/ack", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...response.sync, appliedAt: new Date().toISOString(), components: response.sync.components.map((component) => ({ name: component.name, version: component.version, status: "failed", errorCode: "local_apply_failed" })) }),
      }).catch(() => undefined);
      throw error;
    }
    try {
      await this.request("/api/device/config/ack", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...response.sync, appliedAt: new Date().toISOString(), components: response.sync.components.map((component) => ({ name: component.name, version: component.version, status: "applied" })) }),
      });
    } catch (error) {
      return { changed: response.changed, revision: response.snapshot.configRevision, error: error instanceof Error ? error.message : "Device Config acknowledgement failed" };
    }
    return { changed: response.changed, revision: response.snapshot.configRevision };
  }

  private async request<T>(input: RequestInfo | URL, init?: RequestInit, initFactory?: (attempt: number) => RequestInit): Promise<T> {
    return withSyncRetry(async (attempt) => {
      const requestInit = initFactory ? initFactory(attempt) : init;
      let response: Response;
      try {
        response = await this.fetcher.call(globalThis, input, { ...requestInit, credentials: "same-origin" });
      } catch (error) {
        const wrapped = new Error(error instanceof Error ? error.message : "Device Config request failed");
        Object.assign(wrapped, { retryable: true });
        throw wrapped;
      }
      if (!response.ok) {
        const error = new Error(`Device Config request failed (${response.status})`);
        Object.assign(error, { retryable: isRetryableSyncStatus(response.status) });
        throw error;
      }
      return (await response.json()) as T;
    }, (error) => Boolean(error && typeof error === "object" && "retryable" in error && (error as { retryable?: boolean }).retryable), this.retryPolicy, this.sleep);
  }
}
